"use client";

import { Box, InputAdornment, Stack, Typography } from "@mui/material";
import { IconLock, IconMail, IconShieldLock } from "@tabler/icons-react";
import { useFormik } from "formik";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useState } from "react";
import * as yup from "yup";

import { login } from "@/actions/login";
import CustomFormLabel from "@/app/(protected)/components/forms/theme-elements/CustomFormLabel";
import CustomTextField from "@/app/(protected)/components/forms/theme-elements/CustomTextField";
import { APP_NAME } from "@/app/(protected)/config/constants";
import { loginType } from "@/app/(protected)/types/auth/auth";
import { FormError } from "@/components/form-error";
import { FormSuccess } from "@/components/form-success";
import { resolvePostLoginRedirect } from "@/lib/auth/login-redirect";
import { getAuthErrorMessage } from "@/utils/auth-error-messages";
import { signIn } from "next-auth/react";
import { useRouter } from "next/navigation";
import { SubmitButton } from "./_components/SubmitButton";
import { AuthSocialButtons } from "./social-buttons";

const validationSchema = yup.object({
  email: yup
    .string()
    .email("Email inválido")
    .required("E-mail de preenchimento obrigatório"),
  password: yup.string().required("Senha de preenchimento obrigatório"),
  code: yup.string().optional(),
});

export const LoginForm = ({ title, subtitle, subtext }: loginType) => {
  const searchParams = useSearchParams();
  const router = useRouter();
  const callbackUrl = searchParams?.get("callbackUrl") || null;

  // Mapeia erros da URL para mensagens amigáveis
  const getUrlError = () => {
    const errorParam = searchParams?.get("error");

    const urlErrors: Record<string, string> = {
      OAuthAccountNotLinked: "E-mail já em uso com outro provedor!",
      SessionExpired: "Sua sessão expirou. Faça login novamente.",
      CompanyRemoved:
        "Sua empresa foi removida. Entre em contato com o administrador.",
      AccessRevoked:
        "Seu acesso foi revogado. Entre em contato com o administrador.",
    };

    return errorParam ? urlErrors[errorParam] || "" : "";
  };

  const urlError = getUrlError();

  const [showTwoFactor, setShowTwoFactor] = useState(false);
  const [error, setError] = useState<string | undefined>("");
  const [success, setSuccess] = useState<string | undefined>("");

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      code: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setError("");
      setSuccess("");

      try {
        // Primeiro valida no servidor (2FA, verificações, etc)
        const data = await login(values, callbackUrl);

        // Se data é undefined ou null, houve erro na Server Action
        if (!data) {
          console.error("[LoginForm] Server Action retornou undefined/null");
          setError("Erro ao processar login. Tente novamente.");
          return;
        }

        if (data?.error) {
          formik.resetForm();
          setError(data.error);
          return;
        }

        if (data?.twoFactor) {
          setShowTwoFactor(true);
          return;
        }

        // Se retornou success, verifica se é mensagem informativa ou confirmação de login
        if (data?.success) {
          // Se a mensagem menciona "confirmação" ou "enviado", não tenta logar
          const isEmailConfirmationMessage =
            data.success.toLowerCase().includes("confirmação") ||
            data.success.toLowerCase().includes("enviado");

          if (isEmailConfirmationMessage) {
            setSuccess(data.success);
            formik.resetForm();
            return;
          }

          console.log(
            "[LoginForm] ✅ Validação servidor OK, iniciando signIn...",
          );

          const result = await signIn("credentials", {
            email: values.email,
            password: values.password,
            redirect: false,
          });

          console.log("[LoginForm] 📊 Resultado do signIn:", {
            ok: result?.ok,
            error: result?.error,
            status: result?.status,
            url: result?.url,
          });

          // NextAuth pode retornar ok:true com error presente quando há erro no authorize
          if (result?.error) {
            console.error("[LoginForm] ❌ Erro no signIn:", result.error);
            const errorMessage = getAuthErrorMessage(result.error);
            setError(errorMessage);
            formik.resetForm();
            return;
          }

          if (result?.ok) {
            console.log("[LoginForm] ✅ SignIn OK, redirecionando...");
            setSuccess("Login realizado com sucesso!");
            formik.resetForm();

            const redirectUrl = resolvePostLoginRedirect(callbackUrl);
            console.log("[LoginForm] 🔗 Redirecionando para:", redirectUrl);

            const finalizeRedirect = () => {
              router.replace(redirectUrl);
              router.refresh();
            };

            // Aguarda a sessão ser criada antes de navegar
            window.setTimeout(() => {
              finalizeRedirect();
            }, 700);
          } else {
            console.error("[LoginForm] ⚠️ SignIn não retornou OK");
            setError("Erro ao criar sessão. Tente novamente.");
          }
        }
      } catch (error) {
        console.error("[LoginForm] ❌ Erro capturado:", error);

        // Se for erro de rede ou servidor
        if (error instanceof Error) {
          console.error("[LoginForm] Mensagem de erro:", error.message);
          console.error("[LoginForm] Stack:", error.stack);

          if (error.message.includes("unexpected response")) {
            setError("Erro de comunicação com o servidor. Tente novamente.");
          } else {
            setError(error.message || "Algo deu errado!");
          }
        } else {
          setError("Algo deu errado!");
        }
      }
    },
  });

  return (
    <>
      {title ? (
        <Typography
          fontWeight="700"
          variant="h3"
          mb={1}
          sx={{ textAlign: "center", color: "#1e293b" }}
        >
          {title}
        </Typography>
      ) : null}

      {subtext}

      <form onSubmit={formik.handleSubmit}>
        <Stack spacing={3}>
          {showTwoFactor && (
            <Box>
              <CustomFormLabel
                htmlFor="code"
                sx={{
                  color: "#0f172a",
                  "&.MuiTypography-root": { color: "#0f172a" },
                }}
              >
                Código de Verificação
              </CustomFormLabel>
              <CustomTextField
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconShieldLock
                        size={20}
                        stroke={1.5}
                        style={{ color: "#64748b" }}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "#e5e7eb",
                    },
                    "&:hover fieldset": {
                      borderColor: "#10b981",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#10b981",
                    },
                  },
                  "& .MuiInputBase-input": {
                    color: "#0f172a",
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: "#64748b",
                    opacity: 1,
                  },
                }}
                fullWidth
                id="code"
                name="code"
                placeholder="Digite o código recebido"
                value={formik.values.code}
                onChange={formik.handleChange}
                error={formik.touched.code && Boolean(formik.errors.code)}
                helperText={formik.touched.code && formik.errors.code}
              />
            </Box>
          )}

          {!showTwoFactor && (
            <>
              <Box>
                <CustomFormLabel
                  htmlFor="email"
                  sx={{
                    color: "#0f172a",
                    "&.MuiTypography-root": { color: "#0f172a" },
                  }}
                >
                  E-mail
                </CustomFormLabel>
                <CustomTextField
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <IconMail
                          size={20}
                          stroke={1.5}
                          style={{ color: "#64748b" }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#e5e7eb",
                      },
                      "&:hover fieldset": {
                        borderColor: "#10b981",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#10b981",
                      },
                    },
                    "& .MuiInputBase-input": {
                      color: "#0f172a",
                    },
                    "& .MuiInputBase-input::placeholder": {
                      color: "#64748b",
                      opacity: 1,
                    },
                  }}
                  fullWidth
                  id="email"
                  name="email"
                  type="email"
                  placeholder="seu@email.com"
                  variant="outlined"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                />
              </Box>

              <Box>
                <CustomFormLabel
                  htmlFor="password"
                  sx={{
                    color: "#0f172a",
                    "&.MuiTypography-root": { color: "#0f172a" },
                  }}
                >
                  Senha
                </CustomFormLabel>
                <CustomTextField
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <IconLock
                          size={20}
                          stroke={1.5}
                          style={{ color: "#64748b" }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#e5e7eb",
                      },
                      "&:hover fieldset": {
                        borderColor: "#10b981",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#10b981",
                      },
                    },
                    "& .MuiInputBase-input": {
                      color: "#0f172a",
                    },
                    "& .MuiInputBase-input::placeholder": {
                      color: "#64748b",
                      opacity: 1,
                    },
                  }}
                  fullWidth
                  id="password"
                  name="password"
                  placeholder="Digite sua senha"
                  type="password"
                  variant="outlined"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.password && Boolean(formik.errors.password)
                  }
                  helperText={formik.touched.password && formik.errors.password}
                />
              </Box>

              <Stack direction="row" justifyContent="flex-end">
                <Typography
                  component={Link}
                  href="/auth/reset"
                  variant="subtitle2"
                  fontWeight="500"
                  sx={{
                    textDecoration: "none",
                    color: "#10b981",
                    "&:hover": {
                      textDecoration: "underline",
                      color: "#059669",
                    },
                  }}
                >
                  Esqueceu sua senha?
                </Typography>
              </Stack>
            </>
          )}

          {error && <FormError message={error} />}
          {urlError && <FormError message={urlError} />}
          {success && <FormSuccess message={success} />}
        </Stack>

        <Box mt={4}>
          <SubmitButton
            sx={{
              backgroundColor: "#10b981",
              color: "white",
              fontWeight: 700,
              "&:hover": {
                backgroundColor: "#059669",
              },
              "&:disabled": {
                backgroundColor: "rgba(16, 185, 129, 0.5)",
              },
            }}
          >
            {showTwoFactor ? "Confirmar Código" : "Entrar"}
          </SubmitButton>
        </Box>

        {!showTwoFactor && <AuthSocialButtons title="Entrar com" />}

        <Stack
          direction="row"
          spacing={1}
          mt={3}
          justifyContent="center"
          alignItems="center"
        >
          <Typography color="#64748b" variant="body1" fontWeight="400">
            Novo no {APP_NAME}?
          </Typography>
          <Typography
            component={Link}
            href="/auth/register"
            variant="body1"
            fontWeight="600"
            sx={{
              textDecoration: "none",
              color: "#10b981",
              "&:hover": {
                textDecoration: "underline",
                color: "#059669",
              },
            }}
          >
            Crie sua conta
          </Typography>
        </Stack>
      </form>
    </>
  );
};
